'use client';
import { useRef, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import useOnClickOutside from '@/hooks/useOnClickOutside';

import { Images } from '@/assets/imgs';
import { syntaxHighlight } from '@/helpers/strings';

interface Metadata {
  player_name: string;
  gaming_platform: string;
  game_level: number;
  achievements: string[];
  total_score: number;
  preferred_game_mode: string;
  'collected items': {
    weapon: string;
    armor: string;
    pet: string;
  };
}

interface User {
  img: string;
  name: string;
  player_metadata: Metadata;
}

const users: User[] = [
  {
    img: Images.infrastructure.gamingWebLogo1,
    name: 'MonkeyIslander872',
    player_metadata: {
      player_name: 'MonkeyIslander872',
      gaming_platform: 'PC',
      game_level: 25,
      achievements: ['Master of the Arena', 'Legendary Explorer'],
      total_score: 2000,
      preferred_game_mode: 'Team Deathmatch',
      'collected items': {
        weapon: 'Legendary Sword',
        armor: 'Epic Plate Armor',
        pet: 'Fire Dragon',
      },
    },
  },
  {
    img: Images.infrastructure.gamingWebLogo2,
    name: 'mean_deal_',
    player_metadata: {
      player_name: 'mean_deal_',
      gaming_platform: 'PC',
      game_level: 49,
      achievements: ['Master of Stealth', 'Arcane Scholar', 'Beast Tamer'],
      total_score: 4518,
      preferred_game_mode: 'Capture the Flag',
      'collected items': {
        weapon: 'Spear of Destiny',
        armor: 'Mystic Robes',
        pet: 'Shadow Panther',
      },
    },
  },
  {
    img: Images.infrastructure.gamingWebLogo3,
    name: 'TennisChallenger',
    player_metadata: {
      player_name: 'TennisChallenger',
      gaming_platform: 'Mobile',
      game_level: 48,
      achievements: ['Arena Champion', 'Legendary Hero', 'Master Craftsman'],
      total_score: 1091,
      preferred_game_mode: 'Battle Royale',
      'collected items': {
        weapon: 'Legendary Sword',
        armor: 'Dragon Scale Mail',
        pet: 'Ancient Golem',
      },
    },
  },
  {
    img: Images.infrastructure.gamingWebLogo4,
    name: 'DoozieWoozie',
    player_metadata: {
      player_name: 'DoozieWoozie',
      gaming_platform: 'PC',
      game_level: 24,
      achievements: ['Pathfinder', 'Dragon Slayer', 'Rune Master'],
      total_score: 580,
      preferred_game_mode: 'Battle Royale',
      'collected items': {
        weapon: 'Spear of Destiny',
        armor: 'Epic Plate Armor',
        pet: 'Shadow Panther',
      },
    },
  },
  {
    img: Images.infrastructure.gamingWebLogo5,
    name: 'tiredOfbeIngWired',
    player_metadata: {
      player_name: 'tiredOfbeIngWired',
      gaming_platform: 'Console',
      game_level: 20,
      achievements: ['Speed Runner', 'Treasure Hunter', 'Dungeon Master'],
      total_score: 4951,
      preferred_game_mode: 'Battle Royale',
      'collected items': {
        weapon: 'Legendary Sword',
        armor: 'Shadow Cloak',
        pet: 'Shadow Panther',
      },
    },
  },
  {
    img: Images.infrastructure.gamingWebLogo6,
    name: 'Greendragon_',
    player_metadata: {
      player_name: 'Greendragon_',
      gaming_platform: 'PC',
      game_level: 18,
      achievements: ['Master of Stealth', 'Arcane Scholar', 'Beast Tamer'],
      total_score: 4392,
      preferred_game_mode: 'Co-op Mission',
      'collected items': {
        weapon: 'Legendary Sword',
        armor: 'Epic Plate Armor',
        pet: 'Ice Phoenix',
      },
    },
  },
];

export const GamingWeb = () => {
  const [playerData, setPlayerData] = useState<User | null>();
  const [modalTop, setModalTop] = useState<number | null>();
  const [modalLeft, setModalLeft] = useState<number | null>();
  const [modalRight, setModalRight] = useState<number | null>();
  const [rightAligned, setRightAligned] = useState<boolean>(false);
  const modalRef = useRef(null);
  const [isModalActive, setIsModalActive] = useState(false);

  useOnClickOutside(modalRef, () => setIsModalActive(false));

  const clickHandler = ({
    el,
    row,
    col,
  }: {
    el: User;
    row: number;
    col: number;
  }) => {
    setModalTop((row - 1) * (76 + 24));
    if (typeof window !== 'undefined' && window.innerWidth > 992)
      setModalLeft(col === 1 ? -16 : 0);
    else setModalLeft(-14);
    if (typeof window !== 'undefined' && window.innerWidth > 992)
      setModalRight(col === 2 ? -16 : 0);
    else setModalRight(-14);
    setRightAligned(col === 1 ? false : true);
    setPlayerData(el);
    setIsModalActive(true);
  };

  return (
    <div className="infrastructure-gaming-web">
      <Image
        className="bubbles"
        src={Images.infrastructure.gamingWebBubbles}
        alt="bubbles"
        width={661}
        height={728}
      />
      <Image
        className="bubbles sm"
        src={Images.infrastructure.gamingWebBubblesSm}
        alt="bubbles"
        width={661}
        height={728}
      />
      <div className="container">
        <div className="gaming-main">
          <div className="left">
            <h1 className="integralfc-h2">The New Gaming Web</h1>

            <div className="lexend-body-sm gray300">
              At the core of the XBorg Data Graph Protocol lies a dynamic and
              interconnected network of gaming profiles. Each player's profile
              is represented as a unique ERC-721 NFT, complete with metadata
              unique to their gaming journey.
            </div>
          </div>
          <div className="right">
            <div
              className={clsx(
                'modal',
                rightAligned && 'right-aligned',
                isModalActive && 'active',
              )}
              style={{
                top: modalTop - 16 + 'px',
                left: modalLeft + 'px',
                right: modalRight + 'px',
              }}
              ref={modalRef}
            >
              <Image
                className="cross"
                src={Images.about.cross}
                alt="cross"
                width={40}
                height={40}
                onClick={() => setIsModalActive(false)}
              />
              <div className={clsx('user-badge')}>
                <Image
                  src={playerData?.img}
                  alt={playerData?.name}
                  width={64}
                  height={64}
                />
                <div className="text">{playerData?.name}</div>
              </div>
              <div className="json-body">
                <span className="white">player_metada = </span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: syntaxHighlight(
                      JSON.stringify(playerData?.player_metadata, null, 2),
                    ),
                  }}
                ></span>
              </div>
            </div>
            {users.map((el, idx) => {
              return (
                <div
                  key={el.name}
                  className={clsx('box-container', isModalActive && 'blur')}
                  onClick={() =>
                    clickHandler({
                      el,
                      row: Math.ceil((idx + 1) / 2),
                      col: idx % 2 !== 0 ? 2 : 1,
                    })
                  }
                >
                  <div className="box">
                    <Image src={el.img} alt={el.name} width={64} height={64} />
                    <div className="name">{el.name}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
